/**
 * Term → expected payoff date, by payment frequency (Josh 10/01).
 *
 * `term_days` is the number of PAYMENTS, in the unit the frequency implies (the wizard already labels it
 * "Business Days" / "Weeks" / "Bi-Weekly Payments" / "Months"). The old rule — funding_date + ceil(term × 1.4)
 * calendar days — only ever made sense for daily deals; a 13-week deal "matured" after 19 days (10899).
 *
 *   Daily / Daily (Banking Days Only)  → term business days (Mon–Fri) after funding
 *   Weekly                             → term × 7 calendar days
 *   Bi-Weekly                          → term × 14 calendar days
 *   Monthly                            → term calendar months
 *
 * Dates are YYYY-MM-DD strings; the math is done in UTC so the result never shifts by a day with the browser
 * timezone. Returns null when either input is missing or unusable.
 */

const WEEKEND = new Set([0, 6]);

const parse = (ymd) => {
  if (!ymd) return null;
  const d = new Date(`${String(ymd).slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmt = (d) => d.toISOString().slice(0, 10);

export const termUnit = (frequency) => {
  const f = (frequency || '').toLowerCase().trim();
  if (f.includes('bi-weekly') || f.includes('biweekly') || f.includes('bi weekly')) return 'biweekly';
  if (f.includes('weekly')) return 'weekly';
  if (f.includes('monthly')) return 'monthly';
  return 'daily';
};

export const termUnitLabel = (frequency) => ({
  daily: 'Business Days', weekly: 'Weeks', biweekly: 'Bi-Weekly Payments', monthly: 'Months',
}[termUnit(frequency)]);

/** Business days (Mon–Fri) after `ymd`. */
export const addBusinessDays = (ymd, n) => {
  const d = parse(ymd);
  if (!d) return null;
  let left = Math.max(0, Math.ceil(n));
  while (left > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    if (!WEEKEND.has(d.getUTCDay())) left -= 1;
  }
  return fmt(d);
};

export const expectedPayoffDate = (fundingDate, term, frequency) => {
  const n = parseFloat(term);
  const d = parse(fundingDate);
  if (!d || !(n > 0)) return null;
  switch (termUnit(frequency)) {
    case 'weekly':
      d.setUTCDate(d.getUTCDate() + Math.ceil(n) * 7);
      return fmt(d);
    case 'biweekly':
      d.setUTCDate(d.getUTCDate() + Math.ceil(n) * 14);
      return fmt(d);
    case 'monthly':
      d.setUTCMonth(d.getUTCMonth() + Math.ceil(n));
      return fmt(d);
    default:
      return addBusinessDays(fundingDate, n);
  }
};
